/**
 * Fitment Vehicle Search — ストアフロント用共通JavaScript
 *
 * App Embed（全ページバー）と App Block（セクション内ウィジェット）の
 * 両方で使われるカスケードドロップダウン + 検索機能。
 *
 * API: /apps/fitment?action=years|makes|models|submodels|search|universal
 */

(function () {
  "use strict";

  // Guard against double-loading (embed + block both include this script)
  if (window.__fitmentWidgetLoaded) return;
  window.__fitmentWidgetLoaded = true;

  // API base — Liquid 側 window.ARK_API_BASE (Direct CORS URL) 優先、
  // 無ければ App Proxy フォールバック
  var API_BASE =
    (typeof window !== "undefined" && window.ARK_API_BASE) ||
    "/apps/fitment";
  // shop param (Direct CORS auth に必須) — Liquid から渡される
  var SHOP_DOMAIN =
    typeof window !== "undefined" ? window.ARK_SHOP_DOMAIN || null : null;
  var STORAGE_KEY = "ark.selectedVehicle";
  var COOKIE_KEY = "ark_vehicle";
  var COOKIE_MAX_AGE = 31536000; // 1 year in seconds
  var CASCADE_ORDER = ["year", "make", "model", "submodel"];
  // submodel は optional — Y/M/M で足りる商品もある
  var REQUIRED_CASCADE = ["year", "make", "model"];
  var VEHICLE_CHANGED_EVENT = "ark:vehicle-changed";
  var VEHICLE_CHANGE_REQUEST_EVENT = "ark:vehicle-change-request";
  // Only allow slug-safe characters in product handles
  var SAFE_HANDLE_RE = /^[a-zA-Z0-9\-_]+$/;

  // -----------------------------------------------------------------------
  // API
  // -----------------------------------------------------------------------

  var DBG = function () {
    if (window.ARK_DEBUG !== false) {
      console.log.apply(
        console,
        ["[ark/fitment]"].concat(Array.prototype.slice.call(arguments)),
      );
    }
  };

  function fetchApi(params) {
    var qs = [];
    for (var k in params) {
      if (params[k] != null && params[k] !== "") {
        qs.push(encodeURIComponent(k) + "=" + encodeURIComponent(params[k]));
      }
    }
    // Direct CORS の場合は shop param が必須 (App Proxy 経路では Shopify が自動付与)
    if (SHOP_DOMAIN && !params.shop && API_BASE.indexOf("/apps/") === -1) {
      qs.push("shop=" + encodeURIComponent(SHOP_DOMAIN));
    }
    var url = API_BASE + "?" + qs.join("&");
    DBG("fetch", url);
    return fetch(url)
      .then(function (res) {
        DBG("response", res.status, url);
        if (!res.ok) {
          return res.text().then(function (body) {
            DBG("error body", body);
            throw new Error(
              "Fitment API " + res.status + ": " + body.slice(0, 200),
            );
          });
        }
        return res.json();
      })
      .catch(function (err) {
        DBG("fetch failed", err && err.message);
        throw err;
      });
  }

  function fetchYears() {
    return fetchApi({ action: "years" });
  }
  function fetchMakes(year) {
    return fetchApi({ action: "makes", year: year });
  }
  function fetchModels(year, make) {
    return fetchApi({ action: "models", year: year, make: make });
  }
  function fetchSubmodels(year, make, model) {
    return fetchApi({
      action: "submodels",
      year: year,
      make: make,
      model: model,
    });
  }
  function fetchSearch(year, make, model, submodel, page, limit) {
    return fetchApi({
      action: "search",
      year: year,
      make: make,
      model: model,
      submodel: submodel,
      page: page,
      limit: limit,
    });
  }
  function fetchUniversal(page, limit) {
    return fetchApi({ action: "universal", page: page, limit: limit });
  }

  // -----------------------------------------------------------------------
  // Storage (sessionStorage で車種記憶)
  // -----------------------------------------------------------------------

  function setCookie(name, value, maxAge) {
    try {
      var v = encodeURIComponent(value);
      document.cookie =
        name + "=" + v + "; path=/; max-age=" + maxAge + "; SameSite=Lax";
    } catch (_) {
      /* ignore */
    }
  }

  function deleteCookie(name) {
    try {
      document.cookie = name + "=; path=/; max-age=0; SameSite=Lax";
    } catch (_) {
      /* ignore */
    }
  }

  function getCookie(name) {
    try {
      var parts = document.cookie.split(";");
      for (var i = 0; i < parts.length; i++) {
        var kv = parts[i].split("=");
        if (kv[0].trim() === name) {
          return decodeURIComponent(kv.slice(1).join("=").trim());
        }
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  function broadcastVehicleChange(vehicle) {
    try {
      window.dispatchEvent(
        new CustomEvent(VEHICLE_CHANGED_EVENT, { detail: vehicle }),
      );
    } catch (_) {
      /* ignore */
    }
  }

  function saveVehicle(vehicle) {
    try {
      var json = JSON.stringify(vehicle);
      localStorage.setItem(STORAGE_KEY, json);
      setCookie(COOKIE_KEY, json, COOKIE_MAX_AGE);
      broadcastVehicleChange(vehicle);
    } catch (_) {
      /* ignore */
    }
  }

  function readVehicleSlugFromTag() {
    var tag = document.querySelector("script[data-ark-vehicle-slug]");
    if (!tag) return null;
    try {
      var parsed = JSON.parse(tag.textContent);
      return typeof parsed === "string" ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function loadVehicle() {
    try {
      // 1. URL slug (?v=2022-toyota-tundra-trd-pro) — 最優先 (シェアリンク)
      var slug = readVehicleSlugFromTag() || window.ARK_VEHICLE_SLUG;
      if (slug) {
        var parsed = slugToVehicle(String(slug));
        if (parsed) return parsed;
      }
      // 2. SSR で Liquid が渡した初期値 (例: customer.metafields)
      if (window.ARK_INITIAL_VEHICLE) {
        var initial = window.ARK_INITIAL_VEHICLE;
        if (typeof initial === "string") {
          try {
            return JSON.parse(initial);
          } catch (_) {
            /* fall through */
          }
        } else if (typeof initial === "object") {
          return initial;
        }
      }
      // 3. localStorage / cookie
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      var cookieRaw = getCookie(COOKIE_KEY);
      return cookieRaw ? JSON.parse(cookieRaw) : null;
    } catch (_) {
      return null;
    }
  }

  // slug "2022-toyota-tundra-trd-pro" → vehicle object
  // YEAR + MAKE 単一語 + MODEL 残り (case-insensitive 復元は API 側のオプション照合で行う)
  function slugToVehicle(slug) {
    if (!slug || typeof slug !== "string") return null;
    var parts = slug.split("-").filter(function (p) {
      return p.length > 0;
    });
    if (parts.length < 3) return null;
    var year = parseInt(parts[0], 10);
    if (isNaN(year) || year < 1900 || year > 2100) return null;
    // make + model + submodel の分割は ambiguous なので
    // year + make (next single token) + rest を model+submodel で扱う
    // 詳細な make/model split は restore 時に dropdown options から正引きする
    return {
      year: year,
      make: capitalize(parts[1]),
      model: parts.slice(2).map(capitalize).join(" "),
      submodel: "", // submodel は最初 unset、API が match 候補返したら埋める
      _fromSlug: true,
    };
  }

  function capitalize(s) {
    if (!s) return "";
    return s
      .split(" ")
      .map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(" ");
  }

  function clearVehicle() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      deleteCookie(COOKIE_KEY);
      broadcastVehicleChange(null);
    } catch (_) {
      /* ignore */
    }
  }

  // -----------------------------------------------------------------------
  // DOM helpers (XSS-safe — never use innerHTML with dynamic content)
  // -----------------------------------------------------------------------

  function createOption(value, text) {
    var opt = document.createElement("option");
    opt.value = value;
    opt.textContent = text;
    return opt;
  }

  function clearElement(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  // select に値をセット。厳密一致で入らなければ case-insensitive で option を探し、
  // DB 側の正規 casing を採用する (slug 復元 "?v=2022-ram-1500" → capitalize "Ram" vs DB "RAM" の救済)。
  // 成功時は sel.value が正規値になるので、以降の検索/保存は DB casing で走る。
  function setSelectValueLoose(sel, val) {
    if (!sel || val == null) return false;
    var target = String(val);
    sel.value = target;
    if (sel.value === target) return true;
    var lower = target.toLowerCase();
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value.toLowerCase() === lower) {
        sel.value = sel.options[i].value;
        return true;
      }
    }
    return false;
  }

  // Vehicle slug helpers (URL シェア + SSR 引き継ぎ用)
  function vehicleToSlug(v) {
    if (!v || !v.year || !v.make || !v.model) return "";
    var parts = [v.year, v.make, v.model];
    if (v.submodel) parts.push(v.submodel);
    return parts
      .join("-")
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  // -----------------------------------------------------------------------
  // Widget class
  // -----------------------------------------------------------------------

  function FitmentWidget(container) {
    this.container = container;
    this.mode = container.dataset.fitmentWidget; // "embed" | "block"
    this.showResults = container.dataset.showResults === "true";
    this.resultsLimit = parseInt(container.dataset.resultsLimit, 10) || 24;
    this.showUniversal = container.dataset.showUniversal === "true";
    this.autoSubmit = container.dataset.autoSubmit === "true";
    this.hideSubmit = container.dataset.hideSubmit === "true";
    this.submodelOptional = container.dataset.submodelOptional !== "false"; // default true
    this.currentPage = 1;
    this.currentValues = null;
    this.hasSubmodels = true; // 最初は true、submodels fetch 後に判定
    this.isRestoring = false; // restore 中の auto-submit 抑制

    this.selects = {};
    var self = this;
    CASCADE_ORDER.forEach(function (key) {
      self.selects[key] = container.querySelector(
        '[data-fitment-cascade="' + key + '"]',
      );
    });

    this.searchBtn = container.querySelector("[data-fitment-search]");
    this.clearBtn = container.querySelector("[data-fitment-clear]");
    this.resultsContainer = container.querySelector("[data-fitment-results]");
    this.resultsSummary = container.querySelector(
      "[data-fitment-results-summary]",
    );
    this.resultsGrid = container.querySelector("[data-fitment-results-grid]");
    this.resultsPagination = container.querySelector(
      "[data-fitment-results-pagination]",
    );
    this.selectedLabel = container.querySelector(
      "[data-fitment-selected-label]",
    );

    // Embed bar toggle
    this.toggleBtn = container.querySelector(".fitment-embed-bar__toggle");
    this.dropdown = container.querySelector(".fitment-embed-bar__dropdown");

    this.bindEvents();
    this.init();
  }

  FitmentWidget.prototype.bindEvents = function () {
    var self = this;

    // Cascade events
    CASCADE_ORDER.forEach(function (key, idx) {
      var sel = self.selects[key];
      if (!sel) return;
      sel.addEventListener("change", function () {
        self.onCascadeChange(key, idx);
      });
    });

    // Search button
    if (this.searchBtn) {
      this.searchBtn.addEventListener("click", function () {
        self.onSearch();
      });
    }

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener("click", function () {
        self.onClear();
      });
    }

    // Embed bar toggle
    if (this.toggleBtn && this.dropdown) {
      this.toggleBtn.addEventListener("click", function () {
        var expanded = self.dropdown.hidden;
        self.dropdown.hidden = !expanded;
        self.toggleBtn.setAttribute("aria-expanded", String(expanded));
      });
    }

    // 他 widget からの vehicle change 通知を受けて自身も同期
    window.addEventListener(VEHICLE_CHANGED_EVENT, function (e) {
      var vehicle = e.detail;
      if (!vehicle) {
        // clear からの broadcast → reset
        self.resetToInitial();
        return;
      }
      // 同じ widget からの broadcast は無視 (自分が saveVehicle した分)
      var current = self.getValues();
      if (
        current.year == vehicle.year &&
        current.make == vehicle.make &&
        current.model == vehicle.model &&
        (current.submodel || "") === (vehicle.submodel || "")
      ) {
        return;
      }
      self.restoreVehicle(vehicle);
    });

    // Garage 等からの「車両追加したい」要求 → dropdown 展開 + reset + scroll
    window.addEventListener(VEHICLE_CHANGE_REQUEST_EVENT, function () {
      self.openForNewVehicle();
    });

    // Hide submit button option
    if (this.hideSubmit && this.searchBtn) {
      this.searchBtn.hidden = true;
      this.searchBtn.setAttribute("aria-hidden", "true");
    }
  };

  FitmentWidget.prototype.openForNewVehicle = function () {
    this.resetToInitial();
    clearVehicle();
    if (this.dropdown && this.toggleBtn) {
      this.dropdown.hidden = false;
      this.toggleBtn.setAttribute("aria-expanded", "true");
    }
    try {
      this.container.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (_) {
      this.container.scrollIntoView();
    }
    var firstSelect = this.selects[CASCADE_ORDER[0]];
    if (firstSelect) {
      try {
        firstSelect.focus({ preventScroll: true });
      } catch (_) {
        firstSelect.focus();
      }
    }
  };

  FitmentWidget.prototype.resetToInitial = function () {
    var self = this;
    CASCADE_ORDER.forEach(function (key, idx) {
      if (idx === 0) {
        if (self.selects[key]) self.selects[key].value = "";
      } else {
        self.resetSelect(key);
      }
    });
    this.currentValues = null;
    if (this.searchBtn) this.searchBtn.disabled = true;
    if (this.clearBtn) this.clearBtn.hidden = true;
    if (this.selectedLabel) this.selectedLabel.textContent = "";
    if (this.resultsContainer) this.resultsContainer.hidden = true;
  };

  FitmentWidget.prototype.init = function () {
    var self = this;
    fetchYears()
      .then(function (options) {
        if (!Array.isArray(options)) throw new Error("Invalid response");
        self.populateSelect("year", options);
        self.selects.year.disabled = false;

        // ?addVehicle=1 で来た場合は selectedVehicle 復元せず空状態で開く
        var params = new URLSearchParams(window.location.search);
        if (params.get("addVehicle") === "1") {
          self.openForNewVehicle();
          return;
        }

        var saved = loadVehicle();
        if (saved) {
          return self.restoreVehicle(saved);
        }
      })
      .catch(function (err) {
        DBG("init failed — could not load years", err && err.message);
        self.setPlaceholder("year", "年式の読み込みに失敗");
      });
  };

  FitmentWidget.prototype.restoreVehicle = function (saved) {
    var self = this;
    if (!saved.year) return Promise.resolve();

    this.isRestoring = true;
    // case-insensitive で option 照合 (slug 復元の capitalize と DB casing のズレを吸収)
    if (!setSelectValueLoose(this.selects.year, saved.year)) {
      clearVehicle();
      this.isRestoring = false;
      return Promise.resolve();
    }

    return this.onCascadeChange("year", 0, true)
      .then(function () {
        if (saved.make && self.selects.make) {
          if (!setSelectValueLoose(self.selects.make, saved.make)) {
            clearVehicle();
            return;
          }
          return self.onCascadeChange("make", 1, true).then(function () {
            if (saved.model && self.selects.model) {
              if (!setSelectValueLoose(self.selects.model, saved.model)) {
                clearVehicle();
                return;
              }
              return self.onCascadeChange("model", 2, true).then(function () {
                // submodel は optional — saved に submodel あれば loose セット試行
                // 無効値なら無視 (vehicle 全体は保持)
                if (
                  saved.submodel &&
                  self.selects.submodel &&
                  self.hasSubmodels
                ) {
                  setSelectValueLoose(self.selects.submodel, saved.submodel);
                }
                self.updateSearchButton();
                self.updateSelectedLabel();
              });
            }
          });
        }
      })
      .then(function () {
        self.isRestoring = false;
      });
  };

  FitmentWidget.prototype.onCascadeChange = function (key, idx, silent) {
    var self = this;

    // Reset subsequent selects
    for (var i = idx + 1; i < CASCADE_ORDER.length; i++) {
      this.resetSelect(CASCADE_ORDER[i]);
    }

    this.updateSearchButton();
    if (!silent && this.resultsContainer) {
      this.resultsContainer.hidden = true;
    }

    var val = this.selects[key].value;
    if (!val) return Promise.resolve();

    var nextKey = CASCADE_ORDER[idx + 1];
    if (!nextKey) {
      // 最終ステップ (submodel) を選択した
      this.updateSearchButton();
      if (!silent) {
        this.updateSelectedLabel();
        this.maybeAutoSubmit();
      }
      return Promise.resolve();
    }

    var nextSelect = this.selects[nextKey];
    if (!nextSelect) return Promise.resolve();

    this.setPlaceholder(nextKey, "読み込み中...");

    var values = this.getValues();
    var promise;
    switch (nextKey) {
      case "make":
        promise = fetchMakes(values.year);
        break;
      case "model":
        promise = fetchModels(values.year, values.make);
        break;
      case "submodel":
        promise = fetchSubmodels(values.year, values.make, values.model);
        break;
      default:
        return Promise.resolve();
    }

    return promise
      .then(function (options) {
        if (!Array.isArray(options)) throw new Error("Invalid response");

        // Submodel ステップは optional — 候補ゼロなら hide してそのまま search 可能に
        if (nextKey === "submodel") {
          if (options.length === 0 && self.submodelOptional) {
            self.hasSubmodels = false;
            self.hideSubmodelStep();
            self.updateSearchButton();
            if (!silent) {
              self.updateSelectedLabel();
              self.maybeAutoSubmit();
            }
            return;
          }
          self.hasSubmodels = true;
          self.showSubmodelStep();
        }

        self.populateSelect(nextKey, options);
        nextSelect.disabled = false;
      })
      .catch(function () {
        self.setPlaceholder(nextKey, "選択肢の読み込みに失敗");
      });
  };

  FitmentWidget.prototype.hideSubmodelStep = function () {
    var sel = this.selects.submodel;
    if (!sel) return;
    var group = sel.closest(".fitment-select-group");
    if (group) group.style.display = "none";
    // 値もクリアして submodel ない扱いに
    sel.value = "";
  };

  FitmentWidget.prototype.showSubmodelStep = function () {
    var sel = this.selects.submodel;
    if (!sel) return;
    var group = sel.closest(".fitment-select-group");
    if (group) group.style.display = "";
  };

  FitmentWidget.prototype.maybeAutoSubmit = function () {
    // restore 中 or auto-submit 無効なら何もしない
    if (this.isRestoring || !this.autoSubmit) return;
    var values = this.getValues();
    if (this.isReadyForAutoSubmit(values)) {
      this.onSearch();
    }
  };

  FitmentWidget.prototype.isReadyToSearch = function (values) {
    // 検索ボタン enable 条件: submodel は **fully optional**. Y/M/M で OK
    return !!(values.year && values.make && values.model);
  };

  FitmentWidget.prototype.isReadyForAutoSubmit = function (values) {
    // Auto-submit のみ submodel 候補ある場合は submodel 選択を待つ
    // (Y/M/M で auto-fire すると user が submodel 選ぶ機会を失う)
    if (!this.isReadyToSearch(values)) return false;
    if (this.hasSubmodels && !values.submodel) return false;
    return true;
  };

  FitmentWidget.prototype.getValues = function () {
    var values = {};
    var self = this;
    CASCADE_ORDER.forEach(function (key) {
      var sel = self.selects[key];
      values[key] = sel ? sel.value : "";
    });
    return values;
  };

  FitmentWidget.prototype.populateSelect = function (key, options) {
    var sel = this.selects[key];
    if (!sel) return;

    var defaultLabel = {
      year: "年式を選択",
      make: "メーカーを選択",
      model: "モデルを選択",
      submodel: "サブモデルを選択",
    };

    clearElement(sel);
    sel.appendChild(createOption("", defaultLabel[key] || "選択してください"));
    options.forEach(function (opt) {
      sel.appendChild(createOption(opt.value, opt.label));
    });
  };

  FitmentWidget.prototype.resetSelect = function (key) {
    var sel = this.selects[key];
    if (!sel) return;

    var placeholders = {
      year: "年式を選択",
      make: "先に年式を選択",
      model: "先にメーカーを選択",
      submodel: "先にモデルを選択",
    };

    clearElement(sel);
    sel.appendChild(createOption("", placeholders[key] || "選択してください"));
    sel.disabled = true;
  };

  FitmentWidget.prototype.setPlaceholder = function (key, text) {
    var sel = this.selects[key];
    if (!sel) return;
    clearElement(sel);
    sel.appendChild(createOption("", text));
    sel.disabled = true;
  };

  FitmentWidget.prototype.updateSearchButton = function () {
    var values = this.getValues();
    var ready = this.isReadyToSearch(values);

    if (this.searchBtn) {
      this.searchBtn.disabled = !ready;
    }
  };

  FitmentWidget.prototype.updateSelectedLabel = function () {
    var values = this.getValues();
    if (this.selectedLabel) {
      if (values.year && values.make && values.model) {
        var label = values.year + " " + values.make + " " + values.model;
        if (values.submodel) label += " " + values.submodel;
        this.selectedLabel.textContent = label;
      } else {
        this.selectedLabel.textContent = "";
      }
    }

    if (this.clearBtn) {
      var hasSelection = values.year && values.make;
      this.clearBtn.hidden = !hasSelection;
    }
  };

  FitmentWidget.prototype.onSearch = function () {
    var values = this.getValues();
    // submodel optional — Y/M/M で足りる
    if (!this.isReadyToSearch(values)) return;

    saveVehicle(values);
    this.updateSelectedLabel();

    if (!this.showResults) {
      // 2026-05-18: 新 Vehicle Search Grid block が collection / find-parts page で
      //   localStorage の vehicle を見て render するので、URL に fitment_* を
      //   含める必要はない。slug (?v=...) のみ共有 URL 用に残す。
      // redirect 先は block.settings の `redirect_target` を見て決定 (default: /pages/find-parts)
      var params = new URLSearchParams();
      var slug = vehicleToSlug(values);
      if (slug) params.set("v", slug);
      var target =
        (this.container && this.container.dataset.redirectTarget) ||
        "/pages/find-parts";
      var sep = params.toString() ? "?" : "";
      window.location.href = target + sep + params.toString();
      return;
    }

    this.currentPage = 1;
    this.currentValues = values;
    this.loadResults();
  };

  FitmentWidget.prototype.loadResults = function () {
    var self = this;
    var values = this.currentValues;
    if (!this.resultsContainer || !values) return;

    this.resultsContainer.hidden = false;
    if (this.resultsSummary) {
      this.resultsSummary.textContent = "Searching...";
    }
    if (this.resultsGrid) {
      clearElement(this.resultsGrid);
    }
    // Disable search button during fetch
    if (this.searchBtn) this.searchBtn.disabled = true;

    fetchSearch(
      values.year,
      values.make,
      values.model,
      values.submodel,
      this.currentPage,
      this.resultsLimit,
    )
      .then(function (data) {
        if (!data || !Array.isArray(data.items)) {
          throw new Error("Invalid search response");
        }
        self.renderResults(data, values);

        if (self.showUniversal && self.currentPage === 1) {
          return self.loadUniversalResults();
        }
      })
      .catch(function () {
        if (self.resultsSummary) {
          self.resultsSummary.textContent = "結果の読み込みに失敗しました。";
        }
      })
      .then(function () {
        // Re-enable search button
        self.updateSearchButton();
      });
  };

  FitmentWidget.prototype.loadUniversalResults = function () {
    var self = this;
    return fetchUniversal(1, this.resultsLimit)
      .then(function (data) {
        if (
          !data ||
          !Array.isArray(data.items) ||
          data.items.length === 0 ||
          !self.resultsGrid
        ) {
          return;
        }
        var divider = document.createElement("div");
        divider.className = "fitment-results__divider";
        divider.textContent = "Universal Parts (" + data.total + ")";
        self.resultsGrid.appendChild(divider);

        data.items.forEach(function (item) {
          self.resultsGrid.appendChild(self.createProductCard(item));
        });
      })
      .catch(function () {
        /* ignore universal errors */
      });
  };

  FitmentWidget.prototype.renderResults = function (data, values) {
    if (this.resultsSummary) {
      var vehicleLabel =
        values.year +
        " " +
        values.make +
        " " +
        values.model +
        " " +
        values.submodel;
      if (data.total === 0) {
        this.resultsSummary.textContent = "No parts found for " + vehicleLabel;
      } else {
        this.resultsSummary.textContent =
          data.total + " parts for " + vehicleLabel;
      }
    }

    if (this.resultsGrid) {
      clearElement(this.resultsGrid);
      var self = this;
      data.items.forEach(function (item) {
        self.resultsGrid.appendChild(self.createProductCard(item));
      });
    }

    if (this.resultsPagination) {
      this.renderPagination(data);
    }
  };

  // USD → 表示通貨。Shopify のマルチカレンシー rate (window.Shopify.currency) が
  // 取れれば換算して JPY 等で表示、無ければ USD のまま。
  function formatPrice(priceUsd) {
    var n = parseFloat(priceUsd);
    if (!isFinite(n) || n <= 0) return null;
    try {
      var cur = window.Shopify && window.Shopify.currency;
      if (cur && cur.active && cur.active !== "USD" && cur.rate) {
        var rate = parseFloat(cur.rate);
        if (isFinite(rate) && rate > 0) {
          var converted = n * rate;
          return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: cur.active,
          }).format(converted);
        }
      }
    } catch (_) {
      /* fall through to USD */
    }
    return "$" + n.toFixed(2);
  }

  FitmentWidget.prototype.createProductCard = function (item) {
    var card = document.createElement("a");

    // Sanitize handle: only allow safe slug characters
    var handle = SAFE_HANDLE_RE.test(item.handle)
      ? item.handle
      : encodeURIComponent(item.handle);
    card.href = "/products/" + handle;
    card.className = "fitment-product-card";

    // 商品画像 (https のみ許可 — API 由来だが XSS/insecure 混入を防ぐ)
    if (item.imageUrl && /^https:\/\//.test(item.imageUrl)) {
      var imgWrap = document.createElement("span");
      imgWrap.className = "fitment-product-card__img";
      var img = document.createElement("img");
      img.src = item.imageUrl;
      img.alt = item.imageAlt || item.title || "";
      img.loading = "lazy";
      img.width = 160;
      img.height = 160;
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);
    }

    var body = document.createElement("span");
    body.className = "fitment-product-card__body";

    var title = document.createElement("span");
    title.className = "fitment-product-card__title";
    title.textContent = item.title;
    body.appendChild(title);

    var priceText = item.priceUsd ? formatPrice(item.priceUsd) : null;
    if (priceText) {
      var price = document.createElement("span");
      price.className = "fitment-product-card__price";
      price.textContent = priceText;
      body.appendChild(price);
    }

    card.appendChild(body);
    return card;
  };

  FitmentWidget.prototype.renderPagination = function (data) {
    var totalPages = Math.ceil(data.total / data.limit);
    clearElement(this.resultsPagination);
    if (totalPages <= 1) return;

    var self = this;

    // Prev
    if (this.currentPage > 1) {
      var prev = document.createElement("button");
      prev.className = "fitment-btn fitment-btn--page";
      prev.textContent = "\u2190 Prev";
      prev.addEventListener("click", function () {
        self.currentPage--;
        self.loadResults();
      });
      this.resultsPagination.appendChild(prev);
    }

    // Page info
    var info = document.createElement("span");
    info.className = "fitment-pagination__info";
    info.textContent = "Page " + this.currentPage + " of " + totalPages;
    this.resultsPagination.appendChild(info);

    // Next
    if (this.currentPage < totalPages) {
      var next = document.createElement("button");
      next.className = "fitment-btn fitment-btn--page";
      next.textContent = "Next \u2192";
      next.addEventListener("click", function () {
        self.currentPage++;
        self.loadResults();
      });
      this.resultsPagination.appendChild(next);
    }
  };

  FitmentWidget.prototype.onClear = function () {
    clearVehicle();
    var self = this;
    CASCADE_ORDER.forEach(function (key, idx) {
      if (idx === 0) {
        self.selects[key].value = "";
      } else {
        self.resetSelect(key);
      }
    });

    this.currentValues = null;
    if (this.searchBtn) this.searchBtn.disabled = true;
    if (this.clearBtn) this.clearBtn.hidden = true;
    if (this.selectedLabel) this.selectedLabel.textContent = "";
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
    }
  };

  // -----------------------------------------------------------------------
  // Initialize all widgets on page
  // -----------------------------------------------------------------------

  function initAll() {
    var widgets = document.querySelectorAll("[data-fitment-widget]");
    widgets.forEach(function (el) {
      if (!el._fitmentWidget) {
        el._fitmentWidget = new FitmentWidget(el);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
